import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FileValidationService } from '../../helper/mimetype-allowed';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { DialogComponent } from '../../dialog/confirm.dialog';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SignatureComponent } from 'src/app/leads/leads-managment/tabs/email-sms/signature/signature.component';
import { CameraComponent } from '../../camera/camera.component';
import { RecordingAudioComponent } from '../../recording-audio/recording-audio.component';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TablerIconsModule } from 'angular-tabler-icons';

@Component({
  selector: 'app-attach-section',
  standalone: true,
  imports: [
    CameraComponent,
    RecordingAudioComponent,
    CommonModule,
    MatIcon,
    MatTooltipModule,
    TablerIconsModule
  ],
  templateUrl: './attach-section.component.html',
  styleUrl: './attach-section.component.scss'
})
export class AttachSectionComponent implements OnInit {
  constructor(
    private checkMimtype: FileValidationService,
    private notification: NotificationsService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    // Remove the signature initialization from here
    // as it should come from the parent component
  }

  @Input() currentUser: any = []
  @Input() isSms: Boolean = false
  @Input() showSentButons = false
  @Input() signatures: string = ''
  @Input() showSignature: boolean = true

  @Output() signatureAdd = new EventEmitter<any>();
  @Output() signatureSelect = new EventEmitter<any>();
  @Output() attachFile = new EventEmitter<any>();
  @Output() sendButton = new EventEmitter<any>();
  @Output() trashButton = new EventEmitter<any>();

  isSignatureDropdownVisible = false;
  showAudioRecorder = false;
  activeComponent = false;
  
  // Track attached files to handle duplicates
  attachedFiles: Map<string, number> = new Map();
  
  async onRecordingComplete(audioFile: any) {
    if (audioFile) {
      const allowed = this.checkMimtype.validateFileType(audioFile);
      if (!allowed) {
        this.notification.showClientError('This file is not supported, try another one!');
        return;
      }
  
      const confirmed = await this.callDialog(
        'New voice',
        'Do you want to save this voice?',
      );
      if (confirmed) {
        const originalName = audioFile.name;
        const newName = this.generateUniqueFilename(originalName);
  
        const renamedAudioFile = new File([audioFile], newName, {
          type: audioFile.type,
        });
  
        const audioWithUrl = Object.assign(renamedAudioFile, {
          url: URL.createObjectURL(audioFile),
        });
  
        this.attachFile.emit(audioWithUrl);
      }
    }
  
    this.activeComponent = false;
  }

  openVoiceRecorder() {
    this.showAudioRecorder = !this.showAudioRecorder;
    if (this.showAudioRecorder) {
      this.activeComponent = true
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      const filesArray = Array.from(input.files);

      filesArray.forEach((file) => {
        // Validate file type
        const allowed = this.checkMimtype.validateFileType(file);
        if (!allowed) {
          this.notification.showClientError(
            `The file "${file.name}" is not supported. Please try another one!`
          );
          return;
        }

        // Check for duplicates and rename if necessary
        const originalName = file.name;
        const newFileName = this.generateUniqueFilename(originalName);
        
        // Create a new file with the updated name if needed
        let fileToAttach: File & { url: string };
        if (originalName !== newFileName) {
          fileToAttach = this.renameFile(file, newFileName);
        } else {
          fileToAttach = Object.assign(file, { url: URL.createObjectURL(file) });
        }
        
        this.attachFile.emit(fileToAttach);
      });
    }

    // Clear the input value to allow re-selection of the same files
    input.value = '';
  }
  
  /**
   * Generates a unique filename for duplicate files by adding a number suffix
   */
  generateUniqueFilename(filename: string): string {
    // Check if we've seen this filename before
    if (!this.attachedFiles.has(filename)) {
      this.attachedFiles.set(filename, 1);
      return filename;
    }
    
    // Get file parts
    const lastDotIndex = filename.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
    const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
    
    // Get and increment the counter for this filename
    const counter = this.attachedFiles.get(filename)! + 1;
    this.attachedFiles.set(filename, counter);
    
    // Return new filename with counter
    return `${name} (${counter})${extension}`;
  }
  
  /**
   * Creates a new file with a different name
   */
  renameFile(file: File, newName: string): File & { url: string } {
    const newFile = new File([file], newName, { type: file.type });
    return Object.assign(newFile, { url: URL.createObjectURL(newFile) });
  }

  sendEmailOrSms() {
    this.sendButton.emit()
  }

  cancel() {
    this.trashButton.emit()
  }

  toggleSignatureDropdown() {
    this.isSignatureDropdownVisible = !this.isSignatureDropdownVisible;
  }

  selectSignature() {
    this.isSignatureDropdownVisible = false;
    this.signatureSelect.emit(this.signatures)
  }

  manageSignatures() {
    this.isSignatureDropdownVisible = false
    this.signatureAdd.emit()
  }

  openCamera() {
    const dialogRef = this.dialog.open(CameraComponent, {
      width: '40%',
      height: '60%',
      panelClass: 'custom-dialog',
      data: { isSms: this.isSms }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result instanceof Blob) {
          // Generate unique filename for video with timestamp
          const videoFileName = `recordedVideo_${this.generateUniqueId()}.mp4`;
          // Handle video response
          const videoFile = blobToFile(result, videoFileName);
          const allowed = this.checkMimtype.validateFileType(videoFile);
          if (!allowed) {
            this.notification.showClientError(
              `The file "${videoFile.name}" is not supported. Please try another one!`
            );
            return;
          }
          this.attachFile.emit(videoFile)
        } else if (typeof result === 'string') {
          // Generate unique filename for image with timestamp
          const imageFileName = `capturedImage_${this.generateUniqueId()}.png`;
          const photoFile = base64ToFile(result, imageFileName);

          const allowed = this.checkMimtype.validateFileType(photoFile);
          if (!allowed) {
            this.notification.showClientError(
              `The file "${photoFile.name}" is not supported. Please try another one!`
            );
            return;
          }
          this.attachFile.emit(photoFile)
        }
      }
    });
  }

  // Helper method to generate unique ID based on timestamp and random string
  private generateUniqueId(): string {
    const timestamp = new Date().getTime();
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `${timestamp}_${randomStr}`;
  }

  callDialog(header: string, content: string) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header,
        content,
      },
      width: '500px',
    });

    return dialogRef.afterClosed().toPromise();
  }
}

function base64ToFile(
  base64: string,
  fileName: string,
): File & { url: string } {
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, '');
  const binaryString = atob(base64Data);
  const binaryData = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    binaryData[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([binaryData], { type: 'image/png' });
  const file = new File([blob], fileName, { type: 'image/png' });

  return Object.assign(file, { url: URL.createObjectURL(file) });
}

function blobToFile(blob: Blob, fileName: string): File & { url: string } {
  const file = new File([blob], fileName, { type: blob.type });

  return Object.assign(file, { url: URL.createObjectURL(file) });
}